import { createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useEffect, useMemo, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronDown, ChevronUp, FileText, GripVertical, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { apiDelete, apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { IntakeForm, IntakeQuestion } from "@/lib/db-service/service-intake-forms";
import type { ServiceCatalogItem } from "@/lib/db-service/service-catalog";

export const Route = createFileRoute("/app/formularios")({
  component: Page,
  head: () => ({ meta: [{ title: "Formularios" }] }),
});

const QUESTION_TYPES: Array<{ value: IntakeQuestion["type"]; label: string }> = [
  { value: "text", label: "Texto corto" },
  { value: "textarea", label: "Texto largo" },
  { value: "select", label: "Selección única" },
  { value: "checkbox", label: "Sí / No" },
  { value: "phone", label: "Teléfono" },
];

function newQuestion(): IntakeQuestion {
  return {
    id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: "text",
    label: "",
    required: false,
    options: undefined,
  };
}

type Draft = Partial<IntakeForm> & { questions: IntakeQuestion[] };

function FormBuilder({
  form,
  services,
  onSave,
  onDelete,
  onCancel,
}: {
  form: Draft;
  services: ServiceCatalogItem[];
  onSave: (f: Draft) => void;
  onDelete?: () => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(form);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(form), [form]);

  const addQ = () => setDraft((d) => ({ ...d, questions: [...d.questions, newQuestion()] }));
  const removeQ = (id: string) => setDraft((d) => ({ ...d, questions: d.questions.filter((q) => q.id !== id) }));
  const updateQ = (id: string, patch: Partial<IntakeQuestion>) =>
    setDraft((d) => ({ ...d, questions: d.questions.map((q) => (q.id === id ? { ...q, ...patch } : q)) }));
  const moveQ = (idx: number, dir: -1 | 1) =>
    setDraft((d) => {
      const qs = [...d.questions];
      const target = idx + dir;
      if (target < 0 || target >= qs.length) return d;
      [qs[idx], qs[target]] = [qs[target], qs[idx]];
      return { ...d, questions: qs };
    });

  async function save() {
    if (!draft.name?.trim()) {
      toast.error("Escribí un nombre para el formulario");
      return;
    }
    if (!draft.questions.length) {
      toast.error("Agregá al menos una pregunta");
      return;
    }
    const emptyLabel = draft.questions.find((q) => !q.label.trim());
    if (emptyLabel) {
      toast.error("Todas las preguntas necesitan una etiqueta");
      return;
    }
    setSaving(true);
    try {
      onSave(draft);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5 rounded-xl border bg-card p-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Nombre del formulario</Label>
          <Input
            placeholder="Ej: Formulario inicial de salud"
            value={draft.name ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">
            Servicio <span className="text-muted-foreground">(opcional — si no elegís, aplica a todos)</span>
          </Label>
          <Select
            value={draft.serviceId ?? "__all__"}
            onValueChange={(v) => setDraft((d) => ({ ...d, serviceId: v === "__all__" ? null : v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Todos los servicios" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los servicios</SelectItem>
              {services.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Switch checked={draft.active ?? true} onCheckedChange={(v) => setDraft((d) => ({ ...d, active: v }))} />
          <Label className="cursor-pointer text-sm">Formulario activo</Label>
        </div>
        <Badge variant={draft.active ? "default" : "secondary"} className="text-xs">
          {draft.active ? "Activo" : "Inactivo"}
        </Badge>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Preguntas ({draft.questions.length})</h3>
          <Button size="sm" variant="outline" type="button" onClick={addQ}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Agregar pregunta
          </Button>
        </div>

        {draft.questions.length === 0 && (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <FileText className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Sin preguntas. Agregá la primera.</p>
          </div>
        )}

        {draft.questions.map((q, idx) => (
          <div key={q.id} className="space-y-3 rounded-lg border bg-secondary/10 p-4">
            <div className="flex items-center gap-2">
              <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/40" />
              <span className="text-xs font-medium text-muted-foreground">#{idx + 1}</span>
              <div className="ml-auto flex gap-1">
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={idx === 0} onClick={() => moveQ(idx, -1)}>
                  <ChevronUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  disabled={idx === draft.questions.length - 1}
                  onClick={() => moveQ(idx, 1)}
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                  onClick={() => removeQ(q.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Pregunta / etiqueta</Label>
                <Input
                  placeholder="Ej: ¿Tenés alguna alergia?"
                  value={q.label}
                  onChange={(e) => updateQ(q.id, { label: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tipo de respuesta</Label>
                <Select value={q.type} onValueChange={(v) => updateQ(q.id, { type: v as IntakeQuestion["type"] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {q.type === "select" && (
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Opciones <span className="text-muted-foreground">(una por línea)</span>
                </Label>
                <textarea
                  className="min-h-[80px] w-full resize-none rounded-md border bg-secondary/50 px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder={"Opción 1\nOpción 2\nOpción 3"}
                  value={(q.options ?? []).join("\n")}
                  onChange={(e) =>
                    updateQ(q.id, { options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })
                  }
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <Switch checked={q.required} onCheckedChange={(v) => updateQ(q.id, { required: v })} />
              <Label className="cursor-pointer text-xs">Respuesta obligatoria</Label>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Button onClick={() => void save()} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : draft.id ? "Guardar cambios" : "Crear formulario"}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        {onDelete && (
          <Button variant="ghost" className="ml-auto text-destructive hover:text-destructive" onClick={onDelete}>
            <Trash2 className="mr-1 h-4 w-4" /> Eliminar
          </Button>
        )}
      </div>
    </div>
  );
}

function Page() {
  const tenantId = useWorkspaceTenantId();
  const [forms, setForms] = useState<IntakeForm[]>([]);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  async function load() {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [f, s] = await Promise.all([
        apiGetJson<{ items: IntakeForm[] }>("/api/service-admin?resource=intake", tenantId),
        apiGetJson<{ items: ServiceCatalogItem[] }>("/api/service-admin?resource=services", tenantId),
      ]);
      setForms(f.items ?? []);
      setServices(s.items ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [tenantId]);

  async function saveForm(draft: Draft) {
    if (!tenantId) return;
    try {
      await apiSendJson("/api/service-admin?resource=intake", "POST", draft, tenantId);
      toast.success(draft.id ? "Formulario actualizado" : "Formulario creado");
      setCreating(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo guardar");
    }
  }

  async function deleteForm(id: string) {
    if (!tenantId) return;
    try {
      await apiDelete(`/api/service-admin?resource=intake&id=${id}`, tenantId);
      toast.success("Formulario eliminado");
      setDeleteTarget(null);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo eliminar");
    }
  }

  const blankForm: Draft = { name: "", questions: [], active: true, serviceId: null };
  const activeCount = forms.filter((f) => f.active).length;

  const filteredForms = useMemo(() => {
    if (!search.trim()) return forms;
    const q = search.toLowerCase();
    return forms.filter((f) => f.name.toLowerCase().includes(q));
  }, [forms, search]);

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="formularios" />
      <CrudShellHeader
        title="Formularios"
        subtitle={`${activeCount} activos de ${forms.length}`}
        count={forms.length}
        actions={
          !creating && !editing ? (
            <Button size="sm" className="gap-2" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Nuevo formulario
            </Button>
          ) : undefined
        }
      />
      {loading ? (
        <Loading />
      ) : (
        <div className="grid grid-cols-12 gap-4 px-4 pb-8">
          <aside className="col-span-12 space-y-3 lg:col-span-4">
            {forms.length > 0 ? (
              <Input placeholder="Buscar formularios…" value={search} onChange={(e) => setSearch(e.target.value)} />
            ) : null}
            {filteredForms.length === 0 && !creating ? (
              <div className="rounded-xl border p-8 text-center">
                <FileText className="mx-auto h-10 w-10 text-muted-foreground/30" />
                <p className="mt-2 text-sm text-muted-foreground">Sin formularios</p>
              </div>
            ) : (
              filteredForms.map((form) => (
                <button
                  key={form.id}
                  type="button"
                  onClick={() => {
                    setEditing(form.id);
                    setCreating(false);
                  }}
                  className={cn(
                    "w-full rounded-xl border bg-card p-4 text-left transition-colors hover:border-primary/40",
                    editing === form.id && "border-primary/50 ring-1 ring-primary/20",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <p className="truncate font-medium">{form.name}</p>
                    <Badge variant={form.active ? "default" : "secondary"} className="ml-auto text-[10px]">
                      {form.active ? "Activo" : "Inactivo"}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {(form.questions ?? []).length} preguntas
                    {form.serviceId ? ` · ${services.find((s) => s.id === form.serviceId)?.name ?? "Servicio"}` : " · Todos los servicios"}
                  </p>
                </button>
              ))
            )}
          </aside>

          <section className="col-span-12 lg:col-span-8">
            {creating ? (
              <FormBuilder form={blankForm} services={services} onSave={(f) => void saveForm(f)} onCancel={() => setCreating(false)} />
            ) : editing ? (
              (() => {
                const form = forms.find((f) => f.id === editing);
                if (!form) return null;
                return (
                  <FormBuilder
                    form={{ ...form, questions: form.questions ?? [] }}
                    services={services}
                    onSave={(f) => void saveForm({ ...f, id: form.id })}
                    onDelete={() => setDeleteTarget(form.id)}
                    onCancel={() => setEditing(null)}
                  />
                );
              })()
            ) : (
              <div className="flex min-h-[320px] flex-col items-center justify-center rounded-xl border p-10 text-center">
                <FileText className="mb-4 h-12 w-12 text-muted-foreground/30" />
                <p className="font-medium">Seleccioná un formulario o creá uno nuevo</p>
                <Button size="sm" className="mt-4" onClick={() => setCreating(true)}>
                  <Plus className="mr-1 h-4 w-4" /> Crear formulario
                </Button>
              </div>
            )}
          </section>
        </div>
      )}

      <AlertDialog open={deleteTarget != null} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar formulario?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará de forma permanente y no se podrá recuperar.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && void deleteForm(deleteTarget)}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClinicalPageShell>
  );
}
